import { Translate } from 'next-translate';
import {
  CustomHeaderKey,
  ImportResultsRecord
} from '@components/bid_request/details/types';
import { CustomColumnHeader, Global, LineItem, Lot, LotErrorMap } from 'types';
import { getColumnsAndLotNameFromRow } from './getColumnsAndLotNameFromRow';
import { buildLineItem } from './buildLineItem';
import { newEmptyLot } from './newEmptyLot';
import { updateNewUUIDs } from './updateNewUUIDs';
import { lotValidation } from './lotValidation';

interface CreateNewLotsFromDataProps {
  acceptSuggestedPriceColumn?: boolean,
  customHeaderKeys: CustomHeaderKey[],
  customHeaders: Partial<CustomColumnHeader>[],
  global: Global,
  isPriceUpdate?: boolean,
  lotErrorMap: LotErrorMap,
  rowData: ImportResultsRecord[],
  t: Translate,
}

/**
 * Creates new lots from the given data.
 * @param props
 * @param props.acceptSuggestedPriceColumn {Boolean}
 * @param props.customHeaderKeys {CustomHeaderKey[]}
 * @param props.customHeaders {Partial<CustomColumnHeader>[]}
 * @param props.global {Global}
 * @param props.isPriceUpdate {Boolean}
 * @param props.lotErrorMap {{[index: Number]: String[]}}
 * @param props.rowData {ImportResultsRecord[]}
 * @param props.t {Translate}
 * @return {Lot[], String[], Number[], {[index: Number]: String[]}}
 */
export const createNewLotsFromData = ({
  acceptSuggestedPriceColumn = false,
  customHeaderKeys,
  customHeaders,
  global,
  isPriceUpdate = false,
  lotErrorMap,
  rowData,
  t,
}: CreateNewLotsFromDataProps): [Lot[], number[], { [index: number]: string[] }] => {
  const lotNameToRows: { [key: string]: LineItem[] } = {};
  const updatedLotErrorMap = { ...lotErrorMap };

  rowData.forEach(row => {
    const [rowColumns, lotName] = getColumnsAndLotNameFromRow(row);

    if (!lotNameToRows[lotName as string]) { lotNameToRows[lotName as string] = []; }

    const item = buildLineItem({
      acceptSuggestedPriceColumn,
      customHeaderIndexes: customHeaderKeys,
      global,
      isPriceUpdate,
      row: rowColumns as ImportResultsRecord,
    });

    lotNameToRows[lotName as string].push(item as LineItem);
  });

  const lots: Lot[] = Object.keys(lotNameToRows).map((lotName, index) => {
    const newLot = newEmptyLot(
      lotName,
      updateNewUUIDs(customHeaders as CustomColumnHeader[]),
      index + 1
    );

    newLot.line_items = lotNameToRows[lotName] as LineItem[];
    newLot.line_items_count = newLot.line_items.length;
    return newLot as Lot;
  });

  // Validate the lots and remove any without line items
  const filteredLots = lots.filter(
    lot =>
      (lot.line_items && lot.line_items.length > 0)
      || (lot.errors && lot.errors.length > 0)
  );
  const errorLotIndexes: number[] = [];

  filteredLots.forEach((lot, index) => {
    const lotErrors = lotValidation(t, lot);

    if (lotErrors.length > 0) {
      errorLotIndexes.push(index);
      updatedLotErrorMap[index] = lotErrors;
    }
  });

  return [filteredLots, errorLotIndexes, updatedLotErrorMap];
};
